import React, { useState } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { usePostV4ManufacturerByRouteManufacturerIdRequisitionAndRequisitionNumberEditItemRandmarSkuMutation } from "randmar-api-client";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, TableCell, TableRow, useToast } from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { RequisitionPartNumber } from "../../stores/document/Document";
import ProductDetailsModal from "../products/ProductDetailsModal";
import ButtonWithDialogConfirm from "../util/ButtonWithDialogConfirm";
import CurrencyUtils from "../util/CurrencyUtils";
import InlineSpinner from "../util/InlineSpinner";

interface IProps {
  index: number;
  partNumber: RequisitionPartNumber;
  requisitionNumber: string;
}

const RequisitionPartNumberRow: React.FC<IProps> = ({ partNumber, requisitionNumber }) => {
  const { applicationId } = useManufacturer();
  const [count, setCount] = useState(partNumber.quantityOrdered || 0);
  const [openUpdate, setOpenUpdate] = useState(false);
  const { toast } = useToast();

  const [editItem, { isLoading }] = usePostV4ManufacturerByRouteManufacturerIdRequisitionAndRequisitionNumberEditItemRandmarSkuMutation();
  const hasQuantityChanged = count !== (partNumber.quantityOrdered || 0);

  const handleUpdateButton = () => {
    setOpenUpdate(true);
  };

  const handleCancel = () => {
    setOpenUpdate(false);
  };

  const handleOnCountChanged = (newValue: number) => {
    if (isNaN(newValue) || newValue < 0) newValue = 0;
    setCount(newValue);
  };

  const handleQuantityKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && hasQuantityChanged && !openUpdate && !isLoading) {
      event.preventDefault();
      handleUpdateButton();
    }
  };

  const handleUpdateConfirm = async (): Promise<void> => {
    const quantityDifference = count - (partNumber.quantityOrdered || 0);
    if (quantityDifference === 0) {
      setOpenUpdate(false);
      return;
    }

    const successMessage =
      quantityDifference > 0
        ? `${quantityDifference} x ${partNumber.randmarSKU} added to requisition ${requisitionNumber}`
        : `${Math.abs(quantityDifference)} x ${partNumber.randmarSKU} removed from requisition ${requisitionNumber}`;

    const failMessage =
      quantityDifference > 0
        ? `${quantityDifference} x ${partNumber.randmarSKU} could not be added to requisition ${requisitionNumber}`
        : `${Math.abs(quantityDifference)} x ${partNumber.randmarSKU} could not be removed from requisition ${requisitionNumber}`;

    try {
      await editItem({
        routeManufacturerId: applicationId,
        requisitionNumber,
        randmarSku: partNumber.randmarSKU || "",
        quantity: count,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: successMessage });
    } catch {
      toast({ variant: "destructive", title: "Error", description: failMessage });
    } finally {
      setOpenUpdate(false);
    }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    const successMessage = `${partNumber.randmarSKU} removed from requisition ${requisitionNumber}`;
    const failMessage = `${partNumber.randmarSKU} could not be removed from requisition ${requisitionNumber}`;
    try {
      await editItem({
        routeManufacturerId: applicationId,
        requisitionNumber,
        randmarSku: partNumber.randmarSKU || "",
        quantity: 0,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: successMessage });
    } catch {
      toast({ variant: "destructive", title: "Error", description: failMessage });
    }
  };

  if (!partNumber) return null;

  return (
    <TableRow
      aria-busy={isLoading}
      className={[
        "h-14 border-b border-gray-100 hover:bg-gray-50/60",
        isLoading ? "opacity-70" : "",
      ].filter(Boolean).join(" ")}
    >
      <TableCell>
        <div className="flex flex-col">
          <Link
            to={Path.product(applicationId, partNumber.randmarSKU)}
            className="font-semibold text-slate-900 underline-offset-4 transition-colors duration-200 hover:text-[color:var(--ops-accent)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--ops-focus-ring)]"
          >
            {partNumber.mpn}
          </Link>
          <span className="text-sm text-slate-500">{partNumber.description}</span>
        </div>
      </TableCell>
      <TableCell className="text-center">
        <Input
          className="w-24 text-center"
          type="number"
          min={0}
          value={count}
          aria-label={`Quantity for ${partNumber.mpn}`}
          inputMode="numeric"
          disabled={isLoading}
          onChange={e => handleOnCountChanged(parseInt(e.target.value))}
          onKeyDown={handleQuantityKeyDown}
        />
      </TableCell>
      <TableCell className="text-center">
        {hasQuantityChanged ? (
          <Button
            id="ButtonConfirmItemChange"
            size="sm"
            onClick={handleUpdateButton}
            disabled={openUpdate || isLoading}
            className="rounded-xl"
            aria-label={`Update quantity for ${partNumber.mpn}`}
          >
            Update
          </Button>
        ) : null}
        <Dialog open={openUpdate} onOpenChange={(isOpen) => !isOpen && !isLoading && handleCancel()}>
          <DialogContent className="sm:max-w-[425px]" aria-busy={isLoading}>
            <DialogHeader>
              <DialogTitle className="text-center">Update {partNumber.mpn} quantity?</DialogTitle>
            </DialogHeader>
            {isLoading ? (
              <div className="flex min-h-[80px] items-center justify-center">
                <InlineSpinner size="lg" tone="accent" decorative />
              </div>
            ) : (
              <DialogFooter>
                <Button variant="outline" onClick={handleCancel} className="rounded-xl">
                  Cancel
                </Button>
                <Button onClick={handleUpdateConfirm} className="rounded-xl">
                  Confirm
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      </TableCell>
      <TableCell className="text-center text-slate-700">
        <FormattedNumber value={partNumber.unitPrice || 0} {...CurrencyUtils.format} />
      </TableCell>
      <TableCell className="text-center text-slate-700">
        <FormattedNumber value={(partNumber.unitPrice || 0) * (partNumber.quantityOrdered || 0)} {...CurrencyUtils.format} />
      </TableCell>
      <TableCell className="text-center">
        <ProductDetailsModal randmarSKU={partNumber.randmarSKU || ""} />
      </TableCell>
      <TableCell className="text-center">
        <ButtonWithDialogConfirm
          fab={true}
          onConfirmation={handleDeleteConfirm}
          buttonTitle={`Remove ${partNumber.mpn} from ${requisitionNumber}`}
        />
      </TableCell>
    </TableRow>
  );
};

export default RequisitionPartNumberRow;
